import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithErrorHandling } from "../baseQuery";

export const FAQApi = createApi({
  reducerPath: "FAQApi",
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ["FAQ"],
  refetchOnFocus: true,
  refetchOnReconnect: true,

  endpoints: (builder) => ({
    // Get All FAQ
    getFAQ: builder.query({
      query: ({
        page = 1,
        limit = 10,
        search = "",
        sortBy = "display_order",
        order = "asc",
      } = {}) => ({
        url: "/faq",
        params: { page, limit, search, sortBy, order },
      }),

      /* Normalize API response */
      transformResponse: (response) => ({
        faqs: response?.data ?? [],
        total: response?.total ?? 0,
        page: response?.page ?? 1,
        limit: response?.limit ?? 10,
      }),

      /* Correct tag strategy */
      providesTags: (result) =>
        result?.faqs?.length
          ? [
              ...result.faqs.map(({ id }) => ({
                type: "FAQ",
                id: Number(id),
              })),
              { type: "FAQ", id: "LIST" },
            ]
          : [{ type: "FAQ", id: "LIST" }],

      keepUnusedDataFor: 60,
      refetchOnMountOrArgChange: true,
    }),

    // Get FAQ By ID
    getFAQById: builder.query({
      query: (id) => `/faq/${id}`,
      transformResponse: (response) =>
        response?.data ? response.data : response,
      providesTags: (result, error, id) => [{ type: "FAQ", id }],
    }),

    // Create FAQ
    createFAQ: builder.mutation({
      query: (data) => ({
        url: "/faq",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "FAQ", id: "LIST" }],
    }),

    // Update FAQ
    updateFAQ: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/faq/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "FAQ", id },
        { type: "FAQ", id: "LIST" },
      ],
    }),

    // Delete FAQ
    deleteFAQ: builder.mutation({
      query: (id) => ({
        url: `/faq/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "FAQ", id: "LIST" }],
    }),

    /* Toggle FAQ Status */
    toggleFAQStatus: builder.mutation({
      query: (id) => ({
        url: `/faq/${id}/toggle`, // Assuming this endpoint exists based on the services pattern
        method: "PATCH",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "FAQ", id },
        { type: "FAQ", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetFAQQuery,
  useGetFAQByIdQuery,
  useCreateFAQMutation,
  useUpdateFAQMutation,
  useDeleteFAQMutation,
  useToggleFAQStatusMutation,
} = FAQApi;
